import React from "react";
import _ from "lodash";
import { Map } from "immutable";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import AppConstants from "../../constants/AppConstants";
import BlockUtils from "../../utils/BlockUtils";
import MountFrame from "../MountFrame";
import StageFrameContent from "../StageFrameContent";
import DesignContextUtils from "../../utils/DesignContextUtils";
import LoadingView from "../LoadingView";
import BlurredOverlay from "../BlurredOverlay";
import AppColors from "../../constants/AppColors";

const queryClient = new QueryClient();

const PreviewArea = ({
  preview,
  theme,
  layout,
  width,
  processingState = {},
  pageStyle = {},
}) => {
  const style = {
    position: "relative",
    height: "100%",
    display: "flex",
    justifyContent: "center",
  };

  if (width === "100%") {
    style.width = "100%";
  } else {
    style.flex = `0 0 ${width}px`;
    style.borderRight = `8px solid ${AppColors.GRAY_DARKER}`;
  }

  let frame = null;

  if (preview) {
    const { config: siteConfig = new Map() } = preview;
    const background = _.get(preview, ["backgrounds", layout], false);
    const { blocks } = preview;
    const assetUrl = siteConfig.get("assetURL");
    const designContext = DesignContextUtils.createContext({ assetUrl });

    let frameHeight = 0;
    let layoutWidth = 0;

    let stageTopPad = AppConstants.STAGE_PADDING;
    let stageBottomPad = AppConstants.STAGE_PADDING;

    let topBlock = null;
    let bottomBlock = null;

    if (blocks != null) {
      blocks.forEach((block) => {
        if (BlockUtils.isVisibleInLayout(block, layout)) {
          if (topBlock == null) {
            topBlock = block;
          }
          bottomBlock = block;

          let baseBlock = block;
          if (BlockUtils.hasSiteBlock(block)) {
            baseBlock = block.get(AppConstants.BLOCK_PROP_PARENT_BLOCK);
          }

          frameHeight += baseBlock.getIn([layout, "h"]);
          if (baseBlock.getIn([layout, "w"]) > layoutWidth) {
            layoutWidth = baseBlock.getIn([layout, "w"]);
          }
        }
      });

      if (topBlock != null) {
        stageTopPad += BlockUtils.getBlockTopPadding(topBlock, layout);
      }

      if (bottomBlock != null) {
        stageBottomPad += BlockUtils.getBlockBottomPadding(bottomBlock, layout);
      }
    }

    const { pageBottomOffset } = pageStyle;

    frameHeight += stageTopPad + stageBottomPad + (pageBottomOffset || 0);

    const frameStyle = {
      position: "absolute",
      top: 0,
      minWidth: layoutWidth,
      height: frameHeight,
    };

    const defaultPageStyle = {
      width: layoutWidth,
      paddingTop: stageTopPad,
      paddingBottom: stageBottomPad,
    };

    const finalPageStyle = {
      ...defaultPageStyle,
      ...pageStyle,
    };

    const frameContentProps = {
      designContext,
      background,
      siteConfig,
      theme,
      blocks,
      layout,
      layoutWidth,
      pageStyle: finalPageStyle,
    };

    frame = (
      <MountFrame style={frameStyle} key={`${layout}frame`}>
        <QueryClientProvider client={queryClient}>
          <StageFrameContent {...frameContentProps} />
        </QueryClientProvider>
      </MountFrame>
    );
  }

  const { overlay, message, error } = processingState;

  return (
    <div style={style} className="stage-frame-container">
      {frame}
      <BlurredOverlay show={overlay}>
        <LoadingView message={message} error={error} />
      </BlurredOverlay>
    </div>
  );
};

PreviewArea.defaultProps = {
  width: "100%",
};

export default PreviewArea;
